import React, { useState } from 'react';
import { App, Button, DatePicker, Form, Modal, Tooltip } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { Order } from '../../../types/order';
import { businessDateOf, formatDateEuropean, parseDate } from '../../../utils/dateUtils';
import { useFeedback } from '../../../utils/feedback';
import { getApiErrorMessage } from '../../../utils/apiError';
import { getDisplayOrderNumber } from '../types';
import { updateOrder } from '../api/ordersApi';

/** Business date on every ZK stage; the canonical command owns invoice warnings. */
export default function OrderBusinessDate({ order, canEdit, disabled, onSaved }: {
  order: Order; canEdit: boolean; disabled: boolean; onSaved: () => Promise<unknown>;
}) {
  const { t } = useTranslation(['orders', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const day = businessDateOf(order.orderDate);
  const save = async () => {
    try {
      const values = await form.validateFields();
      const orderDate = values.orderDate.format('YYYY-MM-DD');
      if (orderDate === day) { setOpen(false); return; }
      setSaving(true);
      if (!await updateOrder(order.id, { orderDate }, { modal, t })) return;
      await onSaved();
      setOpen(false);
      feedback.success(t('orders:orderDetail.messages.updateSuccess', { orderNumber: getDisplayOrderNumber(order) }));
    } catch (error: any) {
      if (!error?.errorFields) feedback.error(getApiErrorMessage(error, t, 'orders:orderDetail.messages.updateError', undefined, { orderNumber: getDisplayOrderNumber(order) }));
    } finally { setSaving(false); }
  };
  return <>
    <span data-testid="order-business-date">{formatDateEuropean(day)}</span>
    {canEdit && <Tooltip title={t(disabled ? 'orders:orderDetail.orderDateSaveDraftFirst' : 'orders:orderDetail.editOrderDate')}>
      <Button type="text" size="small" icon={<EditOutlined />} disabled={disabled}
        aria-label={t('orders:orderDetail.editOrderDate')}
        onClick={() => { form.setFieldsValue({ orderDate: parseDate(day) }); setOpen(true); }} />
    </Tooltip>}
    <Modal open={open} title={t('orders:orderDetail.editOrderDate')} onOk={save}
      onCancel={() => setOpen(false)} confirmLoading={saving} cancelButtonProps={{ disabled: saving }}
      closable={!saving} maskClosable={!saving} keyboard={!saving}
      okText={t('common:save')} cancelText={t('common:cancel')}>
      <Form form={form} layout="vertical">
        <Form.Item name="orderDate" label={t('orders:orderDetail.orderDate')}
          extra={t('orders:orderDetail.orderDateHint')}
          rules={[{ required: true, message: t('orders:orderDetail.messages.orderDateRequired') }]}>
          <DatePicker format="DD.MM.YYYY" allowClear={false} />
        </Form.Item>
      </Form>
    </Modal>
  </>;
}
